import { Component, AfterViewInit, NgZone, OnDestroy, OnInit, AfterContentInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, timer as observableTimer } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { NotificationDatabase } from '../../utils/notification/notification-database';
import { NotificationDataSource } from '../../utils/notification/notification-datasource';

import { environment } from '../../../environments/environment';

import { TransactionService } from './../../api/transaction.service';
import { UserProfileService } from '../../api/user-profile.service';
import { HelpService } from '../../api/help.service';
import { map, switchMap } from 'rxjs/operators';
import { CustomerService } from '../../api/customer.service';
@UntilDestroy()
@Component({
  selector: 'iv-top-nav',
  templateUrl: './top-nav.component.html',
  styleUrls: ['./top-nav.component.scss']
})
export class TopNavComponent implements AfterViewInit, OnDestroy, OnInit, AfterContentInit {
  unreadNotificationCount = 0;
  notificationRecords;
  showAddProject = false;
  role: boolean;
  notificationColumns = ['notification', 'dismiss'];
  notificationDatabase: NotificationDatabase;
  notificationDataSource: NotificationDataSource | null;
  imageToShow: any = null;
  isImageLoading = false;
  countryLists = [];
  landingPageCountry;
  routeName = 'landing';
  showReports = true;
  userCountry: string;

  constructor(private transactionService: TransactionService,
    private ngZone: NgZone,
    private router: Router,
    private userProfileService: UserProfileService,
    private helpService: HelpService,
    private customerService: CustomerService) {
    this.notificationDatabase = new NotificationDatabase();
    this.notificationDataSource = new NotificationDataSource(this.notificationDatabase);
  }
  ngOnInit(): void {
    this.userCountry = this.getUserCountry();
    this.populateRouteName();
    this.getProfilePicture();
    this.showReports = environment.showReports;
  }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      observableTimer(100, environment.periodCount)
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.getNotificationsCount();
          });
        });
    });
  }

  ngAfterContentInit() {
    this.populateRouteName();
  }

  populateRouteName() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.routeName = event.url.split('/')[1];
      }
    });
  }

  addPeopleSoftProject() {
    window.open(
      environment.peopleSoftUrl,
      '_blank'
    );
    if (this.showAddProject) {
      this.showAddProject = !this.showAddProject;
    }
  }

  navigateToLandingPage() {
    if (this.showAddProject) {
      this.showAddProject = !this.showAddProject;
    } 
    this.router.navigate([`/customer`], { queryParams: this.customerService.getCustomerLandingQueryParams() });
  }

  openTransaction(transaction) {
    if (transaction && transaction.transaction_type) {
      switch (transaction.transaction_type) {
        case 'consultation':
          this.router.navigate([`/consulting/${transaction.transaction}`]);
          break;
        case 'expense':
          this.router.navigate([`/disbursement/${transaction.transaction}`]);
          break;
        default:
          this.router.navigate([`/${transaction.transaction_type}/${transaction.transaction}`]);
          break;
      }
    }
  }

  getNotificationsCount() {
    this.transactionService.getNotificationCount()
      .pipe(untilDestroyed(this))
      .subscribe(alertsCount => {
        this.unreadNotificationCount = alertsCount;
      });
  }

  getNotificationRecords() {
    this.getNotificationsCount();
    this.transactionService.getNotificationRecords()
      .pipe(untilDestroyed(this))
      .subscribe(notificationRecords => {
        this.notificationDatabase.updateNotificationRecords(notificationRecords);
      });
  }

  dismissNotification(notificationId) {
    this.transactionService.dismissNotification(notificationId)
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.getNotificationRecords();
      });
  }

  dismissAllNotifications() {
    this.transactionService.dismissAllNotifications()
      .pipe(untilDestroyed(this))
      .subscribe(data => {
        this.getNotificationRecords();
      });
  }

  getProfilePicture(): void {
    this.helpService.userAuthenticatedSubject.asObservable()
      .pipe(
        untilDestroyed(this),
        switchMap((token: boolean) => {
          if (token) {
            const getEmployeeId$ = this.userProfileService.getEmployeeId();
            return getEmployeeId$.pipe(
              map((response: any) => response?.result),
              switchMap((employeeInfo: any) => {
                this.countryLists = employeeInfo.country_list;
                this.userProfileService.countryLists = employeeInfo.country_list;
                this.userProfileService.selectCountry({ label: this.getUserCountryLabel() }, false);
                this.landingPageCountry = this.userProfileService.landingPageCountry;
                const getProfilePicture$ = this.userProfileService.getProfilePicture(employeeInfo?.employeeNumber);
                return getProfilePicture$.pipe(
                  map((profilePicture) => {
                    return {
                      'profilePic': profilePicture,
                      'role': employeeInfo.role
                    }
                  })
                );
              })
            )}
          }
        ),
      )
      .subscribe((response: any) => {
        this.showProfilePicture(response.profilePic);
        this.role = !!response.role && response.role === 'Admin' ? true : false;
      });
  }

  selectCountry(event, isReload) {
    this.userProfileService.selectCountry(event, isReload)
  }

  getUserCountryLabel() {
    return this.userProfileService.getCountryLabelLocalStorage();
  }

  showProfilePicture(profilePic) {
    const regEx = new RegExp(`([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{4}|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)`);
    this.isImageLoading = true;
    // need to hard code 'AA==', from ldap we are getting this value.
    if (profilePic && regEx.test(profilePic) && profilePic !== 'AA==') {
      this.imageToShow = `data:image/jpeg;base64,${profilePic}`;
    }
  }

  onClickedOutside(event) {
    if (event === 'outside' && this.showAddProject) {
      this.showAddProject = !this.showAddProject;
    }
  }

  getUserCountry() {
    return this.userProfileService.getCountryLocalStorage();
  }

  disableTopNavClicks() {
    let disabled = false;
    if (window.localStorage.getItem('is_supportuser') === 'true') {
      disabled = true;
    }
    return disabled;
  }
  getRouteName() {
    let flag = false;
    if (this.routeName && (this.routeName === 'landing' || this.routeName.includes('landing'))) {
      flag = false;
    } else {
      flag = true;
    }
    return flag;
  }

  checkUserCountry() {
    let flag = false;
    if (this.getUserCountry() !== 'AUS' || this.getUserCountry() !== 'USA') {
      flag = true;
    } else {
      flag = false;
    }
    return flag;
  }

  get isFinanceUser() {
    return this.userProfileService.getFinanceUserFlagStorage() === 'true';
  }

  navigateToReports() {
    this.router.navigate(['/reports']);
  }
  // this is required for ngx-until-destroy
  ngOnDestroy() { }
}
